import type { SupportCenterAppointmentChannel } from './supportCenterAppointments'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterFormKind,
  SupportCenterTriageStatus,
} from './supportCenterTriage'

export type { SupportCenterAppointmentChannel }

export type SupportCenterDashboardTab = 'volumetria' | 'historico'

export type SupportCenterTriageStatusFilter = SupportCenterTriageStatus | 'none'

/** Parentesco do paciente (Titular derivado de CPFs iguais; demais de relationship_to_holder). */
export type SupportCenterKinshipFilter = 'Titular' | 'Cônjuge' | 'Filho'

export const SUPPORT_CENTER_KINSHIP_FILTER_OPTIONS: Array<{
  value: SupportCenterKinshipFilter
  label: string
}> = [
  { value: 'Titular', label: 'Titular' },
  { value: 'Cônjuge', label: 'Cônjuge' },
  { value: 'Filho', label: 'Filho' },
]

/** Item de busca do catálogo `clients` (filtro dashboard). */
export type SupportCenterClientOption = {
  id: string
  name: string
  id_scgp: number | null
}

export type SupportCenterVolumeFilters = {
  date_from?: string
  date_to?: string
  specialization_id?: string
  /** Escopo do usuário (profissionais vinculados) */
  specialization_ids?: string[]
  professional_user_id?: string
  professional_user_ids?: string[]
  /** Recorte da agenda: e-mails em internal_emails */
  attendant_email?: string
  appointment_statuses?: SupportCenterAppointmentStatus[]
  triage_statuses?: SupportCenterTriageStatusFilter[]
  /** Canais de agendamento. Vazio = todos. */
  channels?: SupportCenterAppointmentChannel[]
  patient_query?: string
  /** Números da apólice (policy_code). Vazio = todos. */
  policy_numbers?: string[]
  /** Códigos do subgrupo (agreement_code). Vazio = todos. */
  convenio_codes?: string[]
  /** Coberturas (support_center_assistances) vinculadas ao agendamento. */
  assistance_ids?: string[]
  /** Origens do encaminhamento (triagem). Vazio = todas. */
  referral_origin_ids?: string[]
  /** Tipos de demanda (triagem). Vazio = todos. */
  demand_type_ids?: string[]
  /** Parentesco (Titular / Cônjuge / Filho). Vazio = todos. */
  kinships?: SupportCenterKinshipFilter[]
  /** Códigos SCGP do corretor (appointments.broker_id). Vazio = todos. */
  broker_codes?: string[]
  /** Códigos SCGP do cliente/empresa (appointments.client_id). Vazio = todos. */
  client_codes?: string[]
}

export type SupportCenterDashboardFilters = {
  dateFrom: string
  dateTo: string
  specializationIds: string[]
  professionalUserIds: string[]
  appointmentStatuses: SupportCenterAppointmentStatus[]
  triageStatuses: SupportCenterTriageStatusFilter[]
  /** Canais de agendamento selecionados. Vazio = todos. */
  channels: SupportCenterAppointmentChannel[]
  patientQuery: string
  /** Números da apólice selecionados. Vazio = todos. */
  policyNumbers: string[]
  /** Códigos do convênio selecionados. Vazio = todos. */
  convenioCodes: string[]
  /** IDs das coberturas (assistências) selecionadas. Vazio = todas. */
  assistanceIds: string[]
  /** Origens do encaminhamento selecionadas. Vazio = todas. */
  referralOriginIds: string[]
  /** Tipos de demanda selecionados. Vazio = todos. */
  demandTypeIds: string[]
  /** Parentesco selecionados. Vazio = todos. */
  kinships: SupportCenterKinshipFilter[]
  /** Códigos SCGP do corretor (appointments.broker_id). Vazio = todos. */
  brokerCodes: string[]
  /** Códigos SCGP do cliente (appointments.client_id). Vazio = todos. */
  clientCodes: string[]
  /** Filtro opcional por e-mail em internal_emails (usado quando aplicável) */
  attendantEmail?: string
}

export type SupportCenterAppointmentListFilters = {
  date_from?: string
  date_to?: string
  specialization_id?: string
  specialization_ids?: string[]
  professional_user_id?: string
  professional_user_ids?: string[]
  appointment_statuses?: SupportCenterAppointmentStatus[]
  triage_statuses?: SupportCenterTriageStatusFilter[]
  /** Canais de agendamento. Vazio = todos. */
  channels?: SupportCenterAppointmentChannel[]
  attendant_email?: string
  /** CPF (parcial/completo) ou nome do paciente */
  patient_query?: string
  /** Números da apólice (policy_code). Vazio = todos. */
  policy_numbers?: string[]
  /** Códigos do subgrupo (agreement_code). Vazio = todos. */
  convenio_codes?: string[]
  /** Coberturas (support_center_assistances) vinculadas ao agendamento. */
  assistance_ids?: string[]
  /** Origens do encaminhamento (triagem). Vazio = todas. */
  referral_origin_ids?: string[]
  /** Tipos de demanda (triagem). Vazio = todos. */
  demand_type_ids?: string[]
  /** Parentesco (Titular / Cônjuge / Filho). Vazio = todos. */
  kinships?: SupportCenterKinshipFilter[]
  /** Códigos SCGP do corretor (appointments.broker_id). Vazio = todos. */
  broker_codes?: string[]
  /** Códigos SCGP do cliente/empresa (appointments.client_id). Vazio = todos. */
  client_codes?: string[]
}

export type SupportCenterVolumeByStatus = Record<SupportCenterAppointmentStatus, number>

export type SupportCenterVolumeBySpecialization = {
  specialization_id: string
  name: string
  total: number
}

export type SupportCenterVolumeByDay = {
  date: string
  total: number
}

export type SupportCenterVolumeByKinship = {
  name: string
  total: number
}

export type SupportCenterVolumeByChannel = {
  channel: SupportCenterAppointmentChannel
  name: string
  total: number
}

/** Agregação por item de catálogo da triagem (origem / tipo de demanda). */
export type SupportCenterVolumeByCatalog = {
  id: string
  name: string
  total: number
}

/** Tipos de demanda agregados por especialidade (abas do gráfico). */
export type SupportCenterVolumeBySpecializationCatalog = {
  specialization_id: string
  name: string
  items: SupportCenterVolumeByCatalog[]
}

export type SupportCenterTriageVolumeByStatus = Record<SupportCenterTriageStatus, number>

/** Percentual 0–100 com uma casa, ou null quando o denominador é zero. */
export type SupportCenterStrategicRate = {
  rate: number | null
  numerator: number
  denominator: number
}

/**
 * Indicadores dos grupos funil, tempo, capacidade, perda e carteira.
 * Calculados sobre colunas já existentes — sem alteração de schema.
 */
export type SupportCenterStrategicMetrics = {
  show_rate: SupportCenterStrategicRate
  absenteeism_rate: SupportCenterStrategicRate
  triage_conversion_rate: SupportCenterStrategicRate
  appointments_without_triage: number
  concluded_without_future: number
  confirmation_rate: SupportCenterStrategicRate
  pre_booking_total: number
  pre_booking_open: number
  triage_pending_count: number
  triage_pending_median_hours: number | null
  wait_median_hours: number | null
  duration_median_minutes: number | null
  punctuality_rate: SupportCenterStrategicRate
  open_forms: number
  occupancy_rate: SupportCenterStrategicRate
  next_free_slot: string | null
  backlog_7: number
  backlog_14: number
  backlog_30: number
  by_professional: SupportCenterVolumeByCatalog[]
  reschedule_rate: SupportCenterStrategicRate
  last_minute_cancel_rate: SupportCenterStrategicRate
  cancel_by_reason: SupportCenterVolumeByCatalog[]
  cancel_by_channel: SupportCenterVolumeByCatalog[]
  unique_patients: number
  appointments_per_patient: number | null
  return_rate: SupportCenterStrategicRate
  forecast_defined: number
  forecast_overdue_without_return: number
  forecast_sessions_short: number
  by_assistance: SupportCenterVolumeByCatalog[]
  by_policy: SupportCenterVolumeByCatalog[]
  by_convenio: SupportCenterVolumeByCatalog[]
  by_age_band: SupportCenterVolumeByCatalog[]
}

export type SupportCenterVolumeMetrics = {
  total: number
  by_status: SupportCenterVolumeByStatus
  cancel_rate: number
  by_specialization: SupportCenterVolumeBySpecialization[]
  by_day: SupportCenterVolumeByDay[]
  by_kinship: SupportCenterVolumeByKinship[]
  by_channel: SupportCenterVolumeByChannel[]
  attendance_by_demand_type: SupportCenterVolumeByCatalog[]
  attendance_by_demand_type_by_specialization: SupportCenterVolumeBySpecializationCatalog[]
  triage_by_status: SupportCenterTriageVolumeByStatus
  triage_by_specialization: SupportCenterVolumeBySpecialization[]
  triage_by_day: SupportCenterVolumeByDay[]
  triage_by_referral_origin: SupportCenterVolumeByCatalog[]
  triage_by_demand_type: SupportCenterVolumeByCatalog[]
  triage_by_demand_type_by_specialization: SupportCenterVolumeBySpecializationCatalog[]
  strategic: SupportCenterStrategicMetrics
}

/** Registro completo de support_center_appointments, com FKs resolvidas para exibição. */
export type SupportCenterAppointmentRecord = {
  id: string
  scheduled_at: string
  status: SupportCenterAppointmentStatus
  /** Canal em que o agendamento foi feito (coluna channel). */
  channel: string | null
  holder_cpf: string
  holder_name: string | null
  patient_cpf: string
  patient_name: string | null
  relationship_to_holder: string | null
  specialty_id: string
  specialization_name: string | null
  assistance_id: string | null
  assistance_name: string | null
  assistance_code: string | null
  /** Kind do formulário da assistência (specific_form_key). */
  form_kind: SupportCenterFormKind | null
  professional_user_id: string | null
  professional_name: string | null
  internal_emails: string[]
  phone_contact_id: string | null
  patient_phone: string | null
  email_contact_ids: string[]
  client_emails: string[]
  call_link: string | null
  /** URL encurtada da reunião (coluna call_link_short). */
  call_link_short: string | null
  outlook_event_id: string | null
  triage_id: string | null
  triage_status: SupportCenterTriageStatus | null
  confirmed_at: string | null
  confirmation_requested_at: string | null
  attendance_started_at: string | null
  attendance_completed_at: string | null
  policy_code: number | null
  agreement_code: number | null
  assistance_provider_code: number | null
  cancel_reason_id: string | null
  cancel_reason_name: string | null
  cancellation_channel: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_by_name: string | null
  created_at: string
  updated_at: string
}

export type SupportCenterHistoryItem = {
  appointment_id: string
  scheduled_at: string
  status: SupportCenterAppointmentStatus
  holder_cpf: string
  patient_cpf: string
  patient_phone: string | null
  client_email: string | null
  holder_name: string | null
  patient_name: string | null
  relationship_to_holder: string | null
  specialization_id: string
  specialization_name: string | null
  form_kind: SupportCenterFormKind | null
  triage_id: string | null
  triage_status: SupportCenterTriageStatus | null
  attendant_email: string | null
  attendant_name: string | null
}

export const APPOINTMENT_STATUS_LABELS: Record<SupportCenterAppointmentStatus, string> = {
  marcado: 'Marcado',
  reagendado: 'Reagendado',
  cancelado: 'Cancelado',
  realizado: 'Realizado',
  nao_realizado: 'Não Realizado',
  pre_agendado: 'Pré-agendado',
}
